import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserReportSummary } from '@tabbio/types';
import { Flag, RotateCw } from '@/components/icons/HugeIcons';
import { useLocale } from '../../../hooks/useLocale';
import { useTheme } from '../../../hooks/useTheme';
import { useUserReports } from '../../../hooks/useMessagingPreferences';
import { userReportStatusTone } from '../../../lib/user-report';

export function ReportsPanel() {
  const { theme } = useTheme();
  const { locale } = useLocale();
  const { t } = useTranslation('common');
  const reports = useUserReports();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2
          style={{
            margin: 0,
            color: theme.text,
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          {t('userReports.title')}
        </h2>
        <p
          style={{ margin: '4px 0 0', color: theme.textTertiary, fontSize: 13 }}
        >
          {t('userReports.subtitle')}
        </p>
      </div>

      {reports.isLoading ? (
        <StateCard text={t('userReports.loading')} />
      ) : reports.isError ? (
        <StateCard
          text={t('userReports.loadFailed')}
          action={
            <button
              type="button"
              onClick={() => void reports.refetch()}
              style={buttonStyle(theme)}
            >
              <RotateCw size={15} />
              {t('userReports.retry')}
            </button>
          }
        />
      ) : reports.data?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.data.map((report) => (
            <ReportCard key={report.id} report={report} locale={locale} />
          ))}
        </div>
      ) : (
        <StateCard
          icon={<Flag size={22} color={theme.textSecondary} />}
          title={t('userReports.emptyTitle')}
          text={t('userReports.emptyBody')}
        />
      )}
    </div>
  );
}

function StateCard({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title?: string;
  text: string;
  action?: ReactNode;
}) {
  const { theme } = useTheme();
  return (
    <div
      style={{
        minHeight: 160,
        borderRadius: 12,
        background: theme.surface,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        textAlign: 'center',
      }}
    >
      {icon}
      {title ? <strong style={{ color: theme.text }}>{title}</strong> : null}
      <span
        style={{ color: theme.textSecondary, fontSize: 13, lineHeight: '19px' }}
      >
        {text}
      </span>
      {action}
    </div>
  );
}

function ReportCard({
  report,
  locale,
}: {
  report: UserReportSummary;
  locale: string;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const tone = userReportStatusTone(report.status);
  const accent =
    tone === 'success'
      ? '#16A34A'
      : tone === 'info'
        ? '#2563EB'
        : theme.textSecondary;
  const badgeBackground =
    tone === 'success'
      ? 'rgba(22, 163, 74, 0.12)'
      : tone === 'info'
        ? 'rgba(37, 99, 235, 0.12)'
        : theme.surfaceSecondary;
  const date = new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en', {
    dateStyle: 'medium',
  }).format(new Date(report.createdAt));

  return (
    <article
      style={{ borderRadius: 12, background: theme.surface, padding: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            dir="auto"
            style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}
          >
            {report.reportedUserName}
          </div>
          <div
            style={{ color: theme.textTertiary, fontSize: 12, marginTop: 2 }}
          >
            {date}
          </div>
        </div>
        <span
          style={{
            color: accent,
            background: badgeBackground,
            borderRadius: 999,
            padding: '5px 9px',
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {t(`userReports.status.${report.status}.label`)}
        </span>
      </div>
      <div
        style={{
          color: theme.textSecondary,
          fontSize: 12,
          fontWeight: 600,
          marginTop: 12,
        }}
      >
        {t(`reportProfile.categories.${report.category}`)}
      </div>
      <p
        style={{
          color: theme.textSecondary,
          fontSize: 13,
          lineHeight: '19px',
          margin: '6px 0 0',
        }}
      >
        {t(`userReports.status.${report.status}.description`)}
      </p>
      <div style={{ color: theme.textTertiary, fontSize: 11, marginTop: 10 }}>
        {t('userReports.reference', { id: report.id.slice(-8).toUpperCase() })}
      </div>
    </article>
  );
}

function buttonStyle(theme: { border: string; text: string }): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 36,
    padding: '0 14px',
    borderRadius: 999,
    border: `1px solid ${theme.border}`,
    background: 'transparent',
    color: theme.text,
    cursor: 'pointer',
  };
}
